import { Component, Input, Output, EventEmitter, forwardRef, OnInit, HostListener, ElementRef, ViewChild, Injector, afterNextRender } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HcPhoneCountryCode } from '@nx-c4g/c4g-models';
import { getCountryCodes } from '@nx-c4g/c4g-models';
import { TranslateService, TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'c4g-phone-country-code-selector',
  templateUrl: './phone-country-code-selector.component.html',
  styleUrls: ['./phone-country-code-selector.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneCountryCodeSelectorComponent),
      multi: true
    }
  ]
})
export class PhoneCountryCodeSelectorComponent implements ControlValueAccessor, OnInit {
  private static nextTriggerId = 0;

  readonly triggerId = `c4g-phone-cc-trigger-${PhoneCountryCodeSelectorComponent.nextTriggerId++}`;

  @Input() label = 'Country Code';
  @Input() placeholder = 'Select country code';
  @Input() required = false;
  @Output() valueChange = new EventEmitter<string>();

  @ViewChild('searchInput') private searchInputRef?: ElementRef<HTMLInputElement>;

  countryCodes: HcPhoneCountryCode[] = [];
  filteredCountryCodes: HcPhoneCountryCode[] = [];
  selectedCountryCode: HcPhoneCountryCode | null = null;
  searchText = '';
  isOpen = false;
  disabled = false;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private translate: TranslateService,
    private elementRef: ElementRef,
    private injector: Injector
  ) {}

  ngOnInit(): void {
    this.countryCodes = getCountryCodes();
    this.filteredCountryCodes = [...this.countryCodes];
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  writeValue(value: string): void {
    if (value) {
      this.selectedCountryCode = this.countryCodes.find(cc => cc.phoneCountryCode === value) || null;
    } else {
      this.selectedCountryCode = null;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    if (!this.disabled) {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.searchText = '';
        this.filteredCountryCodes = [...this.countryCodes];
        afterNextRender(
          () => this.searchInputRef?.nativeElement?.focus(),
          { injector: this.injector }
        );
      }
    }
  }

  onSearchChange(): void {
    const searchLower = this.searchText.toLowerCase();
    this.filteredCountryCodes = this.countryCodes.filter(cc => {
      const countryName = this.getCountryName(cc).toLowerCase();
      const phoneCode = cc.phoneCountryCode.toLowerCase();
      const countryId = cc._id.toLowerCase();
      return countryName.includes(searchLower) ||
             phoneCode.includes(searchLower) ||
             countryId.includes(searchLower);
    });
  }

  selectCountryCode(countryCode: HcPhoneCountryCode): void {
    this.selectedCountryCode = countryCode;
    this.onChange(countryCode.phoneCountryCode);
    this.onTouched();
    this.valueChange.emit(countryCode.phoneCountryCode);
    this.isOpen = false;
    this.searchText = '';
  }

  getCountryName(countryCode: HcPhoneCountryCode): string {
    return this.translate.instant(countryCode.nameKey);
  }

  getDisplayValue(): string {
    if (this.selectedCountryCode) {
      return `${this.selectedCountryCode.flagIcon} ${this.getCountryName(this.selectedCountryCode)} (${this.selectedCountryCode.phoneCountryCode})`;
    }
    return this.placeholder;
  }
}

